import React, { useState, useEffect } from 'react';
import { Card, Stack, Button, Box } from '@mui/material';
import PropTypes from 'prop-types';
import {
  LISTING_ACTIONS,
  LISTING_COLUMNS,
  SUB_LISTING_COLUMNS,
} from '../../config/module-configs/workspace.config';
import ListData from '../table-elements/list-data.component';
import Iconify from '../common/iconify.component';
import {
  deleteDataById,
  getFieldsListById,
  getListData,
} from '../../services/workspace.service';
import { ACTIONS } from '../../config/const.config';
import ConfirmPopup from '../common/confirm-popup.component';
import AddEditWorkspace from './add-edit-workspace.component';
import ViewWorkspace from './view-workspace.component';
import SnackbarInfo from '../common/snackbar-info.component';
import ManageSequenceWorkspaceFields from './manage-sequence-workspace-fields.component';
import useFilteredActions from '../../hooks/filtered-action.hook';
import useAccess from '../../hooks/access.hook';
import useAbortableRequest from '../../hooks/abortable-request.hook';

const ListWorkspace = ({ openFilters }) => {
  const columns = LISTING_COLUMNS;
  const subRowColumns = SUB_LISTING_COLUMNS;
  const actions = useFilteredActions('workspaces', LISTING_ACTIONS);

  const { getSignal, isAbortError } = useAbortableRequest();

  const { hasOperationAccess } = useAccess();

  const [options, setOptions] = useState({
    page: 0,
    rowsPerPage: 100,
    totalRows: null,
    rows: [],
    reloadCounter: 0,
    error: false,
    loading: true,
  });
  const [doAction, setDoAction] = useState({
    data: null,
    action: null,
  });
  const [snackbarInfo, setSnackbarInfo] = useState({
    show: false,
    type: '',
    message: '',
  });

  const handleShowSubRow = (rowData) => {
    if (!rowData?.subRows || (rowData?.subRows && !rowData.subRows.length)) {
      setOptions({
        ...options,
        rows: options.rows.map((item) =>
          item.id === rowData.id
            ? { ...item, subRowsLoading: true, subRows: [] }
            : item
        ),
      });

      getFieldsListById(rowData.id)
        .then((res) => {
          setOptions({
            ...options,
            rows: options.rows.map((item) =>
              item.id === rowData.id
                ? { ...item, subRowsLoading: false, subRows: res?.data }
                : item
            ),
          });
        })
        .catch(() => {
          // Skipped
        });
    } else {
      setOptions({
        ...options,
        rows: options.rows.map((item) =>
          item.id === rowData.id
            ? { ...item, subRowsLoading: false, subRows: [] }
            : item
        ),
      });
    }
  };

  const handleRefreshData = () => {
    setOptions({
      ...options,
      reloadCounter: options.reloadCounter + 1,
      loading: true,
    });
  };

  const handleActionSelect = (selectedAction, selectedData) => {
    setDoAction({
      ...doAction,
      data: selectedData || null,
      action: selectedAction || null,
    });
  };
  const handleActionCancel = () => {
    setDoAction({
      ...doAction,
      data: null,
      action: null,
    });
  };
  const handleActionSuccess = (message = '') => {
    setSnackbarInfo({
      ...snackbarInfo,
      show: false,
    });
    if (message) {
      setTimeout(() => {
        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'success',
          message,
        });
      }, 10);
    }

    handleActionCancel();
    handleRefreshData();
  };

  const handleDeleteData = () => {
    setSnackbarInfo({
      ...snackbarInfo,
      show: false,
    });

    deleteDataById(doAction.data.id)
      .then(() => {
        handleActionSuccess('Workspace deleted successfully.');
      })
      .catch((err) => {
        const message =
          err.response?.data?.message ||
          'Something went wrong, please try again.';

        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message,
        });
        handleActionCancel();
      });
  };

  useEffect(() => {
    const signal = getSignal();
    const params = [];
    params.push(`page=${options.page + 1}`);
    params.push(`perPage=${options.rowsPerPage}`);
    if (options.sortBy && options.sortOrder) {
      params.push(`sortBy=${options.sortBy}`);
      params.push(`sortOrder=${options.sortOrder}`);
    }

    const paramsQuery = params.length > 0 ? `?${params.join('&')}` : '';
    getListData(paramsQuery, signal)
      .then((res) => {
        if (!signal.aborted) {
          setOptions({
            ...options,
            loading: false,
            rows: res?.data || [],
            error: false,
          });
        }
      })
      .catch((err) => {
        if (isAbortError(err)) {
          return;
        }
        setOptions({
          ...options,
          loading: false,
          rows: [],
          error: true,
        });
      });
  }, [options.reloadCounter]);

  return (
    <Box
      sx={{
        transition: 'margin 0.3s ease, width 0.3s ease',
        width: openFilters ? 'calc(100% - 300px)' : '100%',
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        sx={{ px: 0, py: 2 }}
      >
        <div>
          {hasOperationAccess('workspaces', 'create') && (
            <Button
              color="primary"
              variant="contained"
              startIcon={<Iconify icon="ic:round-add" />}
              onClick={() => handleActionSelect(ACTIONS.CREATE.value, null)}
            >
              Create New
            </Button>
          )}
        </div>
        <div>
          <Button
            color="inherit"
            variant="contained"
            startIcon={<Iconify icon="ic:twotone-refresh" />}
            onClick={handleRefreshData}
            disabled={options.loading}
          >
            Refresh
          </Button>
        </div>
      </Stack>

      <Card>
        <ListData
          columns={columns}
          rows={options.rows}
          page={options.page}
          rowsPerPage={options.rowsPerPage}
          totalRows={options.totalRows}
          loading={options.loading}
          actions={actions}
          error={options.error}
          sortBy={options.sortBy}
          sortOrder={options.sortOrder}
          onPageChange={() => {}}
          onRowsPerPageChange={() => {}}
          onSortChange={() => {}}
          onAction={handleActionSelect}
          subRowColumns={subRowColumns}
          handleShowSubRow={handleShowSubRow}
        />
      </Card>

      {doAction.action === ACTIONS.CREATE.value && (
        <AddEditWorkspace
          title="Create New Workspace"
          dataId={0}
          onCancel={handleActionCancel}
          onSuccess={handleActionSuccess}
        />
      )}

      {doAction.action === ACTIONS.EDIT.value && (
        <AddEditWorkspace
          title={`Edit Workspace Details - ${doAction.data.name}`}
          dataId={doAction.data.id}
          onCancel={handleActionCancel}
          onSuccess={handleActionSuccess}
        />
      )}

      {doAction.action === ACTIONS.VIEW.value && (
        <ViewWorkspace
          title={`Workspace Details - ${doAction.data.name}`}
          dataId={doAction.data.id}
          onCancel={handleActionCancel}
        />
      )}

      {doAction.action === ACTIONS.DELETE.value && (
        <ConfirmPopup
          title="Delete Workspace"
          message={`Do you want to delete ${doAction.data.name} workspace? You can not undo this action!`}
          onCancel={handleActionCancel}
          onSuccess={handleDeleteData}
          successButtonText="Delete"
          cancelButtonText="Cancel"
          showConfirmationInput
          confirmationInputText="delete"
        />
      )}

      {doAction.action === ACTIONS.MANAGE_ORDER.value && (
        <ManageSequenceWorkspaceFields
          title={`Reorder Workspace Fields - ${doAction.data.name}`}
          dataId={doAction.data.id}
          onCancel={handleActionCancel}
        />
      )}

      {snackbarInfo.show && (
        <SnackbarInfo type={snackbarInfo.type} message={snackbarInfo.message} />
      )}
    </Box>
  );
};

ListWorkspace.propTypes = {
  openFilters: PropTypes.bool.isRequired,
};
export default ListWorkspace;
